// Small sound manager for Chess Corner.
// Sounds live in /public/sounds (Kenney, CC0 - see public/sounds/CREDITS.txt).
// The files are already levelled against each other, so they all share one volume.
import {useEffect, useState} from "react";

// "start" is not in the list yet: no game start sound has been picked. Calls to playSound("start") are ignored
// until a start.mp3 is added to public/sounds and "start" is added back here.
const SOUND_NAMES = [
    "move", "capture", "castle", "check", "promote",
    "victory", "defeat", "draw",
    "notify", "lowtime", "illegal"
];

const VOLUME = 0.8;

const STORAGE_KEY = "chesscorner.muted";

const readMuted = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === "true";
    } catch (e) {
        return false;
    }
};

let muted = readMuted();
const listeners = new Set();

const soundUrl = (name) => `${process.env.PUBLIC_URL || ""}/sounds/${name}.mp3`;

// ---------------------------------------------------------------------------------------------------------------
// Web Audio: every sound is downloaded once, decoded, and kept in memory. Playing it is then instant and can't fail
// on the network (the old way, one <audio> element per play, sometimes stayed silent when a file request failed or
// the browser had unloaded an idle player).
// ---------------------------------------------------------------------------------------------------------------
const AudioContextClass = typeof window !== "undefined" ? (window.AudioContext || window.webkitAudioContext) : null;
let context = null;
let output = null;
const files = {};      // name -> Promise<ArrayBuffer>, the downloaded file
const buffers = {};    // name -> AudioBuffer, ready to play
const decoding = {};   // name -> Promise<AudioBuffer>

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Downloads a sound, trying again a few times if the connection hiccups
const download = (name) => {
    if (!files[name]) {
        files[name] = (async () => {
            for (let attempt = 1; ; attempt++) {
                try {
                    const response = await fetch(soundUrl(name), {cache: "force-cache"});
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return await response.arrayBuffer();
                } catch (e) {
                    if (attempt >= 4) throw e;
                    await wait(500 * attempt);
                }
            }
        })();
        // A failed download may be tried again later
        files[name].catch(() => delete files[name]);
    }
    return files[name];
};

// Older Safari only has the callback version of decodeAudioData
const decode = (data) => new Promise((resolve, reject) => {
    const result = context.decodeAudioData(data, resolve, reject);
    if (result && typeof result.then === "function") result.then(resolve, reject);
});

const load = (name) => {
    if (buffers[name]) return Promise.resolve(buffers[name]);
    if (!decoding[name]) {
        decoding[name] = download(name)
            // decodeAudioData takes over the data it is given, so it gets a copy (the download is kept for retries)
            .then((data) => decode(data.slice(0)))
            .then((buffer) => {
                buffers[name] = buffer;
                return buffer;
            });
        decoding[name].catch(() => delete decoding[name]);
    }
    return decoding[name];
};

// Browsers only allow sound after the visitor has clicked or pressed a key, so the audio is started on the first
// interaction (and woken up again if the browser paused it, as phones do when you switch apps)
const ensureContext = () => {
    if (!AudioContextClass) return null;
    if (!context) {
        try {
            context = new AudioContextClass();
            output = context.createGain();
            output.gain.value = VOLUME;
            output.connect(context.destination);
            SOUND_NAMES.forEach((name) => load(name).catch(() => {}));
        } catch (e) {
            context = null;
            return null;
        }
    }
    if (context.state !== "running") context.resume().catch(() => {});
    return context;
};

if (typeof window !== "undefined" && AudioContextClass) {
    const unlock = () => ensureContext();
    ["pointerdown", "keydown", "touchend"].forEach((event) => window.addEventListener(event, unlock, {capture: true, passive: true}));
}

const playBuffer = (buffer) => {
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(output);
    source.start(0);
};

// ---------------------------------------------------------------------------------------------------------------
// Fallback for browsers without Web Audio: the old way with a few <audio> elements per sound
// ---------------------------------------------------------------------------------------------------------------
const pools = {};
const getPool = (name) => {
    if (!pools[name]) {
        pools[name] = {index: 0, clips: [0, 1, 2].map(() => {
            const audio = new Audio(soundUrl(name));
            audio.preload = "auto";
            audio.volume = VOLUME;
            return audio;
        })};
    }
    return pools[name];
};
const playWithElement = (name) => {
    if (typeof Audio === "undefined") return;
    const pool = getPool(name);
    const clip = pool.clips[pool.index];
    pool.index = (pool.index + 1) % pool.clips.length;
    try {
        clip.currentTime = 0;
        const result = clip.play();
        if (result && typeof result.catch === "function") result.catch(() => {});
    } catch (e) {
        // ignore
    }
};

// Starts downloading every sound (decoding waits for the first click), so the first move already has its sound
export const preloadSounds = () => {
    if (!AudioContextClass) {
        if (typeof Audio !== "undefined") SOUND_NAMES.forEach(getPool);
        return;
    }
    SOUND_NAMES.forEach((name) => download(name).catch(() => {}));
};

export const playSound = (name) => {
    if (muted || !SOUND_NAMES.includes(name)) return;
    const ctx = ensureContext();
    if (!ctx) {
        playWithElement(name);
        return;
    }
    if (buffers[name]) {
        playBuffer(buffers[name]);
        return;
    }
    // Not ready yet (only right after the page opened): play it if it arrives quickly, otherwise skip it,
    // because a sound that comes late is more confusing than none
    const requested = Date.now();
    load(name)
        .then((buffer) => {
            if (Date.now() - requested < 400) playBuffer(buffer);
        })
        .catch(() => playWithElement(name));
};

export const isMuted = () => muted;

export const setMuted = (value) => {
    muted = value;
    try {
        localStorage.setItem(STORAGE_KEY, String(value));
    } catch (e) {
        // ignore
    }
    listeners.forEach((listener) => listener(value));
};

export const useMuted = () => {
    const [value, setValue] = useState(muted);
    useEffect(() => {
        listeners.add(setValue);
        return () => listeners.delete(setValue);
    }, []);
    return [value, setMuted];
};

// Picks the right sound for a chess.js move object. `isCheck` should describe the position AFTER the move.
export const soundForMove = (move, isCheck) => {
    if (!move) return "move";
    if (isCheck || (move.san && move.san.includes("+"))) return "check";
    if (move.promotion) return "promote";
    const flags = move.flags || "";
    if (flags.includes("k") || flags.includes("q")) return "castle";
    if (move.captured || flags.includes("c") || flags.includes("e")) return "capture";
    return "move";
};

export const playMoveSound = (move, isCheck) => playSound(soundForMove(move, isCheck));
